import { Link } from 'react-router-dom'

export default function Landing() {
  return (
    <div>
      <section className="hero">
        <span className="hero-eyebrow">REST · JSON · API keys</span>
        <h1 className="hero-title">Build with Chosen.</h1>
        <p className="hero-lede">
          Drive your ATS programmatically. Manage candidates and jobs, push
          analytics into your BI tool, automate sourcing &amp; outreach —
          everything you can do in the app, with a key.
        </p>
        <p style={{ fontStyle: 'italic', color: 'var(--fg-muted)', fontSize: '0.875rem', marginTop: '-0.5rem' }}>
          REST, JSON, and the occasional honest 500.
        </p>
        <div className="cta-row">
          <Link to="/reference" className="cta primary">
            Open the API reference →
          </Link>
          <Link to="/guides/authentication" className="cta">
            Auth quickstart
          </Link>
        </div>
      </section>

      <h2>Quickstart</h2>
      <div className="quick-start">
        <div className="step-card">
          <p className="step-num">Step 1</p>
          <h3 className="step-title">Get a key</h3>
          <p className="step-body">
            In the Chosen app, go to <strong>Settings → Developers</strong>{' '}
            and create an API key. Choose the role for least-privilege.
          </p>
        </div>
        <div className="step-card">
          <p className="step-num">Step 2</p>
          <h3 className="step-title">Send a request</h3>
          <p className="step-body">
            Use the key as a Bearer token. Permissions follow the role
            attached to the key.
          </p>
        </div>
        <div className="step-card">
          <p className="step-num">Step 3</p>
          <h3 className="step-title">Try it now</h3>
          <p className="step-body">
            Paste your key into the <Link to="/reference">API reference</Link>
            's "Try it" pane to make live requests against your own data.
          </p>
        </div>
      </div>

      <h3>Your first call</h3>
      <pre>
        <code>{`curl https://hire.chosenhq.com/api/v1/jobs \\
  -H "Authorization: Bearer cho_YOUR_KEY_HERE"`}</code>
      </pre>

      <p>
        Every endpoint accepts the same Bearer token. The key's role determines
        which routes you can call — for instance, a Viewer-scoped key can{' '}
        <code>GET /candidates</code> but not <code>POST /candidates</code>.
      </p>

      <h2>What you can do</h2>
      <div className="resource-grid">
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Candidates</p>
          <p className="resource-card-body">
            Create, update, search, archive, bulk import.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Jobs</p>
          <p className="resource-card-body">
            CRUD, publish/unpublish, interview plans, audit log.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Applications</p>
          <p className="resource-card-body">
            Link candidates to jobs, move through stages.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Scheduling</p>
          <p className="resource-card-body">
            Requests, meetings, templates, freebusy.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Analytics</p>
          <p className="resource-card-body">
            Pipeline metrics, funnel, time-to-hire — read-only.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Sourcing</p>
          <p className="resource-card-body">
            Web-search prospects, enrich, outreach campaigns.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Exports</p>
          <p className="resource-card-body">
            Async data exports with status polling.
          </p>
        </Link>
        <Link to="/reference" className="resource-card">
          <p className="resource-card-title">Deep Search</p>
          <p className="resource-card-body">
            Long-running natural-language candidate searches.
          </p>
        </Link>
      </div>

      <h3>Before you build</h3>
      <p>
        Some endpoints cost real money (web searches, email enrichment,
        AI scoring). The{' '}
        <Link to="/guides/rate-limits">Rate Limits guide</Link> lays out the
        per-tier limits and the per-domain throttle on outreach sends.
        Reading it first will save you a 429.
      </p>
    </div>
  )
}
